import { useState, useRef, useEffect } from 'react';
import { Icon } from '@/components/Icon'
import { useImmer } from 'use-immer';
import { validateEmail } from '@/shared/utils';
import { publicAction } from '@/data/loaders';
import { Spinner } from '@/components/Spinner';
import { useFormField } from '@/form/FormProvider';
import { useUser } from '@/systems/Authentication';

export function ShareBox({ ...args }) {

  const { name, value: people, updateValue: updatePeople, error, setError, hint } = useFormField({
    validateValue: ({value, required}) => {
      if ((value || []).length == 0) {
        return required;
      }
    },
    formatValue: (value, pretty) => (value || []).filter((person) => validateEmail(person.email) && person.status !== "not eligible").map((person) => { return { email: person.email, _id: person._id } }),
    parseValue: (value) => value || [],
    getFocusElement: ({name, parent}) => parent.querySelector(`.share_box_outer[name="${name}"] input`),
    ...args
  });

  const { userdata } = useUser();
  const my_organization_id = userdata.organization_id;

  const [value, setValue] = useState("");
  // const [people, updatePeople] = useImmer([]);
  const inputRef = useRef(null);
  const shareboxRef = useRef(null);
  const resultsRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(0);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const lastPersonRef = useRef(null);
  const [searchResults, updateSearchResults] = useImmer(null);
  
  const shouldShowResults = () => {
    return isFocusWithin && searchResults?.results && searchResults.results.length > 0;
  };

  function removePerson(person) {
    updatePeople((draft) => {
      draft.splice(draft.findIndex((p) => p.email === person.email), 1);
    });
    inputRef.current.focus();
  }

  function addPerson({ email, name, _id, status, organization_id }) {
    updatePeople((draft) => {
      if (!draft.find((p) => p.email === email)) {
        draft.push({name: name, email: email, _id: _id, status: status, organization_id: organization_id});
      }
    });
  }

  useEffect(() => {
    people.forEach((person) => {
      if (!person._id && !person.status) {
        if (validateEmail(person.email)) {
          updatePeople((draft) => {
            let index = draft.findIndex((p) => p.email === person.email);
            if (index > -1) {
              draft[index].status = "searching";
            }
          });
          publicAction("getPerson", {
            email: person.email
          }).then(function (response) {
            if (response.data.user) {
              updatePeople((draft) => {
                let index = draft.findIndex((p) => p.email === person.email);
                if (index > -1) {
                  draft[index].status = "found";
                  draft[index].name = response.data.user.name;
                  draft[index]._id = response.data.user._id;
                  draft[index].organization_id = response.data.user.organization_id;
                }
              });
            } else if (response.data.valid) {
              updatePeople((draft) => {
                let index = draft.findIndex((p) => p.email === person.email);
                if (index > -1) {
                  draft[index].status = "not found";
                  draft[index].organization_id = response.data.organization_id;
                }
              });
            } else {
              updatePeople((draft) => {
                let index = draft.findIndex((p) => p.email === person.email);
                if (index > -1) {
                  draft[index].status = "not eligible";
                }
              });
            }
          }).catch(function (error) {
            console.error(error);
            updatePeople((draft) => {
              let index = draft.findIndex((p) => p.email === person.email);
              if (index > -1) {
                draft[index].status = "error";
              }
            });
          });
        }
      }
    });
  }, [people, updatePeople]);

  function onTab(value, event) {
    if (value.trim().length > 0) {
      if (searchResults?.results.length > 0) {
        addPerson(searchResults.results[0]);
        setValue("");
        searchChange("");
        event.preventDefault();
      }
    }
  }

  function onEnter(value) {
    if (value.trim().length > 0) {
      if (searchResults?.results.length > 0) {
        addPerson(searchResults.results[0]);
      } else {
        addPerson({ email: value });
      }
    }
    setValue("");
    searchChange("");
  }
  
  function onChange(value, recursive = false) {
    // Recursively check the beginning for a new entry
    if (value.trim().indexOf(",") > -1 || value.trim().indexOf("\n") > -1) {
      let split_index = value.trim().indexOf(",") > -1 ? value.trim().indexOf(",") : value.trim().indexOf("\n");
      if (split_index > 0) {
        addPerson({ email: value.trim().substring(0, split_index) });
      }
      setValue(value.trim().substring(split_index + 1).trim());
      onChange(value.trim().substring(split_index + 1).trim(), true);
    } else if (value.trim().indexOf(" ") > -1 && validateEmail(value.trim().split(" ")[0])) {
      let split_index = value.trim().indexOf(" ");
      addPerson({ email: value.trim().substring(0, split_index) });
      let new_value = value.trim().substring(split_index + 1);
      setValue(new_value.trim());
      onChange(new_value.trim(), validateEmail(new_value.trim()));
    } else if (recursive && value.trim().length > 0) {
      addPerson({ email: value.trim() });
      setValue("");
      searchChange("");
    } else {
      searchChange(value);
    }
  }

  useEffect(() => {
    const handleFocusIn = () => {
      setIsFocusWithin(true);
    };
    
    const handleFocusOut = (event) => {
      const relatedTarget = event.relatedTarget;
      const isWithinComponent = 
        (shareboxRef.current && shareboxRef.current.contains(relatedTarget)) ||
        (resultsRef.current && resultsRef.current.contains(relatedTarget));
      
      if (!isWithinComponent) {
        setIsFocusWithin(false);
      }
    };
    
    const handleClickOutside = (event) => {
      const isWithinComponent = 
        (shareboxRef.current && shareboxRef.current.contains(event.target)) ||
        (resultsRef.current && resultsRef.current.contains(event.target));
      
      if (!isWithinComponent) {
        setIsFocusWithin(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    const sharebox = shareboxRef.current;
    const results = resultsRef.current;
    
    if (sharebox) {
      sharebox.addEventListener('focusin', handleFocusIn);
      sharebox.addEventListener('focusout', handleFocusOut);
    }
    
    if (results) {
      results.addEventListener('focusin', handleFocusIn);
      results.addEventListener('focusout', handleFocusOut);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      
      if (sharebox) {
        sharebox.removeEventListener('focusin', handleFocusIn);
        sharebox.removeEventListener('focusout', handleFocusOut);
      }
      
      if (results) {
        results.removeEventListener('focusin', handleFocusIn);
        results.removeEventListener('focusout', handleFocusOut);
      }
    };
  }, [searchResults]);
  
  useEffect(() => {
    inputRef.current.focus();
  }, []);

  function searchChange(value) {
    if (value.trim().length > 0) {
      let search_time = Date.now();
      publicAction("searchPeople", {
        query: value
      }).then(function (response) {
        updateSearchResults(draft => {
          if (!draft?.search_time || draft.search_time < search_time) {
            draft = {
              search_time: search_time,
              results: response.data.results.filter((result) => !people.find((person) => person.email === result.email)).filter((result) => {
                if (validateEmail(value.trim())) {
                  if (result.email.toLowerCase().startsWith(value.trim().toLowerCase())) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              }).map((result) => {
                if (result._id) {
                  return result;
                } else {
                  return {
                    status: "not found",
                    email: result.email,
                    organization_id: result.organization_id
                  }
                }
              })
            }
            return draft;
          }
        });
      }).catch(function (error) {
        console.error(error);
      });
    } else {
      updateSearchResults({
        search_time: Date.now(),
        results: []
      });
    }
  }

  useEffect(() => {
    if (shareboxRef.current) {
      setAvailableHeight(shareboxRef.current.getBoundingClientRect().bottom);
    }
  }, [people]);

  const handleResultKeyDown = (e, result, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      addPerson(result);
      setValue("");
      searchChange("");
      inputRef.current.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextSibling = e.target.nextElementSibling;
      if (nextSibling) {
        nextSibling.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        inputRef.current.focus();
      } else {
        const prevSibling = e.target.previousElementSibling;
        if (prevSibling) {
          prevSibling.focus();
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      inputRef.current.focus();
    } else if (e.key === 'Tab') {
      if (index === searchResults.results.length - 1) {
        setIsFocusWithin(false);
      }
    }
  };

  const hasInvalidPeople = people.some((person) => !validateEmail(person.email));
  const hasIneligiblePeople = people.some((person) => person.status === "not eligible");
  const hasNotFoundPeople = people.some((person) => person.status === "not found");
  const hasOutsideOrgPeople = people.some((person) => my_organization_id && person.organization_id !== undefined && person.organization_id !== my_organization_id);

  return (
    <div className="share_box_outer" name={name}>
      <div 
        className="share_box" 
        ref={shareboxRef} 
        onClick={() => inputRef.current.focus()}
      >
        {people.map((person, index) => (
          <div 
            className={"pill" + ((!validateEmail(person.email) || person.status === "not eligible") ? " invalid" : ((my_organization_id && person.organization_id !== undefined && person.organization_id !== my_organization_id) ? " outside_org" : ""))} 
            key={person.email}
          >
            <div className="pill_icon">
              {person.status === "searching" ? <Spinner /> : <Icon name={validateEmail(person.email) ? (person.status === "not eligible" ? "cancel" : (person.status == "not found" ? "email-outline" : "account-circle-outline")) : "alert-circle-outline"} />}
            </div>
            <div className="pill_text" title={person.email}>{person.name || person.email}</div>
            <div 
              className="pill_remove" 
              tabIndex={0} 
              onClick={() => removePerson(person)} 
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Backspace') {
                  e.preventDefault();
                  removePerson(person);
                }
              }} 
              ref={index === people.length - 1 ? lastPersonRef : null}
              role="button"
              aria-label={`Remove ${person.name || person.email}`}
            >
              <Icon name="close" />
            </div>
          </div>
        ))}
        <input 
          type="text" 
          value={value} 
          placeholder={people.length > 0 ? "" : "Emails or names"} 
          onChange={(e) => {
            setValue(e.target.value);
            onChange(e.target.value);
          }} 
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEnter(e.target.value);
            } else if (e.key === "Tab") {
              onTab(e.target.value, e);
            } else if (e.key === 'Backspace') {
              if (value.length === 0) {
                if (lastPersonRef.current) {
                  lastPersonRef.current.focus();
                }
              }
            } else if (e.key === 'ArrowDown' && shouldShowResults()) {
              e.preventDefault();
              const firstResult = resultsRef.current?.firstChild;
              if (firstResult) {
                firstResult.focus();
              }
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.target.blur();
            }
          }}
          ref={inputRef}
          aria-controls={shouldShowResults() ? "search-results-list" : undefined}
          aria-expanded={shouldShowResults()}
          role="combobox"
          aria-autocomplete="list"
        />
      </div>
      {shouldShowResults() ? (
        <div 
          className="share_box_results" 
          style={{maxHeight: availableHeight - 50}}
          ref={resultsRef}
          id="search-results-list"
          role="listbox"
        >
          {searchResults.results.map((result, index) => (
            <div 
              className="share_box_result" 
              key={result._id || result.email} 
              onClick={() => {
                addPerson(result);
                setValue("");
                searchChange("");
                inputRef.current.focus();
              }} 
              tabIndex={0}
              role="option"
              aria-selected={false}
              onKeyDown={(e) => handleResultKeyDown(e, result, index)}
            >
              <div>
                <Icon name={result._id ? "account-circle-outline" : "email-outline"} />
              </div>
              <div>
                {result.name ? <div>{result.name}</div> : null}
                <div>{result.email}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {error ? <div className="input_error">{error}</div> : (hint ? <div className="input_hint">{hint}</div> : null)}
      {hasInvalidPeople ? (
        <div className="share_box_error_banner error_background">
          <div><Icon name="alert-circle-outline" /></div>
          <div>Some emails are invalid.</div>
        </div>
      ) : null}
      {hasOutsideOrgPeople ? (
        <div className="share_box_error_banner warning_background">
          <div><Icon name="domain-off" /></div>
          <div>Be careful. Some emails are outside of your organization.</div>
        </div>
      ) : null}
      {hasIneligiblePeople ? (
        <div className="share_box_error_banner error_background">
          <div><Icon name="cancel" /></div>
          <div>Some emails are outside of your organization and not eligible for access.</div>
        </div>
      ) : null}
      {hasNotFoundPeople ? (
        <div className="share_box_error_banner">
          <div><Icon name="email-outline" /></div>
          <div>Some people don't have an account yet, so we'll email them to join.</div>
        </div>
      ) : null}
    </div>
  )
}